import { FunctionComponent, PropsWithChildren, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useUserContext } from "../../user/contexts/user.context";
import { ParamsNames } from "../constantes/paramsNames";
import i18n from "../../../i18n";

// Locales valides supportées par l'application
const VALID_LOCALES = ["en-US", "fr-FR"];
const DEFAULT_LOCALE = "en-US";

/**
 * Composant qui gère la détection du code organisation depuis l'URL
 * et la redirection automatique si le code organisation n'est pas présent
 */
export const OrganizationUrlHandler: FunctionComponent<PropsWithChildren<unknown>> = ({ children }) => {
  const params = useParams<{ [key: string]: string }>();
  const history = useHistory();
  const location = useLocation();
  const { hasUser, setOrganizationFromUrl, currentOrganization, getUser, changeOrganizationByCode } = useUserContext();

  const organizationCodeFromUrl = params[ParamsNames.ORGANIZATION_CODE];
  const localeFromUrl = params[ParamsNames.LOCALE];
  
  // Référence pour suivre le dernier code organisation traité
  // Cela évite de retraiter le même code organisation plusieurs fois
  const lastProcessedOrgCodeRef = useRef<string | null>(null);
  
  // Référence pour suivre le dernier chemin où on a ajouté la locale
  // Cela évite les boucles infinies de redirection
  const lastLocaleProcessedPathRef = useRef<string | null>(null);
  
  // Fonction pour obtenir la locale (depuis l'URL, i18next, ou par défaut)
  const getLocale = useCallback((): string => {
    if (localeFromUrl && VALID_LOCALES.includes(localeFromUrl)) {
      return localeFromUrl;
    }
    // Utiliser la locale de i18next si disponible, sinon DEFAULT_LOCALE
    const i18nLocale = i18n.language || DEFAULT_LOCALE;
    // Normaliser la locale (i18next peut retourner "fr" mais on veut "fr-FR")
    if (i18nLocale === "fr") {
      return "fr-FR";
    } else if (i18nLocale === "en") {
      return "en-US";
    }
    // Si la locale de i18next est valide, l'utiliser
    if (VALID_LOCALES.includes(i18nLocale)) {
      return i18nLocale;
    }
    return DEFAULT_LOCALE;
  }, [localeFromUrl]);
  
  // Fonction pour détecter si la locale est présente dans le pathname
  const hasLocaleInPath = useCallback((path: string): boolean => {
    const pathParts = path.split('/').filter(p => p);
    return pathParts.length > 0 && VALID_LOCALES.includes(pathParts[0]);
  }, []);

  // EFFET 0 : Ajouter la locale et l'organisation à l'URL si elles sont absentes
  // Intercepter l'appel initial et forcer un rechargement complet avec la locale et l'organisation
  useEffect(() => {
    const currentPath = location.pathname;
    if (!currentPath) {
      return;
    }

    // Ne pas traiter les routes de login et d'erreur
    if (currentPath === "/login" || currentPath === "/404" || currentPath === "/error" || currentPath.startsWith("/login")) {
      lastLocaleProcessedPathRef.current = null;
      sessionStorage.removeItem('locale-redirect-in-progress');
      return;
    }

    // Vérifier si l'URL contient des paramètres non résolus (signe d'un problème)
    if (currentPath.includes(':locale') || currentPath.includes(':organizationCode')) {
      // L'URL contient des paramètres non résolus, corriger l'URL
      console.error('URL contains unresolved parameters, fixing:', currentPath);
      
      // Vérifier si une redirection est déjà en cours
      const redirectInProgress = sessionStorage.getItem('locale-redirect-in-progress');
      if (redirectInProgress) {
        return;
      }
      
      // Extraire le chemin réel en enlevant les paramètres non résolus
      let fixedPath = currentPath
        .replace(/\/:locale/g, '')
        .replace(/\/:organizationCode/g, '')
        .replace(/:locale/g, '')
        .replace(/:organizationCode/g, '');
      
      // Nettoyer les doubles slashes
      fixedPath = fixedPath.replace(/\/+/g, '/');
      if (fixedPath === '/') {
        fixedPath = '/';
      } else if (!fixedPath.startsWith('/')) {
        fixedPath = '/' + fixedPath;
      }
      
      // Construire la nouvelle URL avec la locale et l'organisation
      const locale = getLocale();
      
      // Essayer d'obtenir l'organisation depuis l'utilisateur si disponible
      let orgCode: string | undefined;
      try {
        const user = getUser();
        orgCode = user?.organizationList?.[0]?.code;
      } catch (e) {
        // L'utilisateur n'est pas encore chargé, utiliser l'organisation depuis l'URL si disponible
        const orgMatch = fixedPath.match(/\/organization\/([^/]+)/);
        orgCode = orgMatch ? orgMatch[1] : undefined;
      }
      
      if (orgCode) {
        const newPath = `/${locale}/organization/${orgCode}${fixedPath}`;
        const newUrl = `${window.location.origin}${newPath}${window.location.search}${window.location.hash}`;
        console.log('Redirecting to fixed URL:', newUrl);
        sessionStorage.setItem('locale-redirect-in-progress', 'true');
        window.location.href = newUrl;
      } else {
        // Si on n'a pas d'organisation, attendre que l'utilisateur soit chargé
        // L'effet 1 s'occupera de corriger l'URL
        return;
      }
      return;
    }

    // Vérifier si la locale est présente dans le pathname OU dans les params
    const hasLocale = hasLocaleInPath(currentPath) || !!localeFromUrl;
    
    // Vérifier si une organisation est présente dans l'URL
    const orgMatch = currentPath.match(/\/organization\/([^/]+)/);
    const hasOrganization = !!orgMatch;
    
    // Si la locale ET l'organisation sont déjà présentes, réinitialiser la référence et nettoyer le flag
    if (hasLocale && hasOrganization) {
      lastLocaleProcessedPathRef.current = null;
      sessionStorage.removeItem('locale-redirect-in-progress');
      return;
    }

    // Vérifier si une redirection est déjà en cours (protection contre les boucles)
    const redirectInProgress = sessionStorage.getItem('locale-redirect-in-progress');
    if (redirectInProgress) {
      // Une redirection est déjà en cours, ne pas rediriger à nouveau
      return;
    }

    // Si on a déjà traité ce chemin exact, ne pas le refaire
    if (lastLocaleProcessedPathRef.current === currentPath) {
      return;
    }

    // Marquer qu'on va traiter ce chemin
    lastLocaleProcessedPathRef.current = currentPath;

    // Utiliser la locale par défaut (depuis i18next ou "en-US")
    const locale = getLocale();
    
    // PRIORITÉ : Utiliser l'organisation de l'URL si elle est présente, sinon la première de la liste
    let orgCode: string | undefined;
    if (hasOrganization) {
      // L'organisation est dans l'URL, la préserver (elle a la priorité)
      orgCode = orgMatch![1];
    } else if (hasUser) {
      // Pas d'organisation dans l'URL, utiliser la première de la liste de l'utilisateur
      try {
        const user = getUser();
        orgCode = user?.organizationList?.[0]?.code;
      } catch (e) {
        // L'utilisateur n'est pas encore chargé
        orgCode = undefined;
      }
    }
    
    // Si l'utilisateur n'est pas encore chargé et qu'on n'a pas d'organisation, attendre
    if (!hasUser && !hasOrganization) {
      lastLocaleProcessedPathRef.current = null;
      return;
    }
    
    // Si on n'a toujours pas d'organisation après avoir essayé de la récupérer, attendre
    if (!orgCode) {
      lastLocaleProcessedPathRef.current = null;
      return;
    }
    
    // Extraire le chemin sans la locale et l'organisation
    let pathWithoutLocaleAndOrg = currentPath;
    const pathParts = pathWithoutLocaleAndOrg.split('/').filter(p => p);
    
    // Enlever la locale si elle est présente
    if (pathParts.length > 0 && VALID_LOCALES.includes(pathParts[0])) {
      pathParts.shift();
    }
    
    // Enlever l'organisation si elle est présente
    if (pathParts.length >= 2 && pathParts[0] === 'organization') {
      pathParts.shift(); // Enlever "organization"
      pathParts.shift(); // Enlever le code organisation
    }
    
    pathWithoutLocaleAndOrg = pathParts.length > 0 ? "/" + pathParts.join("/") : "/";
    
    // Construire le nouveau chemin avec la locale et l'organisation
    const newPath = `/${locale}/organization/${orgCode}${pathWithoutLocaleAndOrg}`;
    
    // Construire l'URL complète avec le protocole et le host
    const newUrl = `${window.location.origin}${newPath}${window.location.search}${window.location.hash}`;
    
    // Vérifier que le nouveau chemin est différent et ne contient pas de paramètres non résolus
    if (currentPath !== newPath && !newPath.includes(':locale') && !newPath.includes(':organizationCode')) {
      // Marquer qu'une redirection est en cours
      sessionStorage.setItem('locale-redirect-in-progress', 'true');
      
      // Utiliser window.location.href pour forcer un rechargement complet
      window.location.href = newUrl;
    } else {
      // Si le chemin n'a pas changé ou contient des paramètres non résolus, réinitialiser la référence
      lastLocaleProcessedPathRef.current = null;
      sessionStorage.removeItem('locale-redirect-in-progress');
    }
  }, [location.pathname, getLocale, hasLocaleInPath, localeFromUrl, getUser, hasUser]);

  // EFFET 1 : Initialiser l'organisation dès que les données utilisateur sont disponibles
  // Cette logique s'exécute quand hasUser devient true ou quand le code organisation dans l'URL change
  useEffect(() => {
    if (!hasUser) {
      lastProcessedOrgCodeRef.current = null;
      return;
    }

    const currentPath = location.pathname;
    if (!currentPath) {
      return;
    }

    // Ne pas traiter les routes de login et d'erreur
    if (currentPath === "/login" || currentPath === "/404" || currentPath === "/error" || currentPath.startsWith("/login")) {
      return;
    }

    // Vérifier si l'URL contient des paramètres non résolus (signe d'un problème)
    if (currentPath.includes(':locale') || currentPath.includes(':organizationCode')) {
      // L'URL contient des paramètres non résolus, ne pas traiter
      console.error('URL contains unresolved parameters in EFFET 1:', currentPath);
      return;
    }

    // Extraire le code organisation du pathname
    const orgMatch = currentPath.match(/\/organization\/([^/]+)/);
    const orgCodeInPath = orgMatch ? orgMatch[1] : null;
    const codeToUse = organizationCodeFromUrl || orgCodeInPath;
    
    // Si on a déjà traité ce code organisation, ne pas retraiter
    if (codeToUse && lastProcessedOrgCodeRef.current === codeToUse && currentOrganization?.code === codeToUse) {
      return;
    }

    try {
      const user = getUser();
      
      // Vérifier que la liste des organisations est disponible
      if (!user.organizationList || user.organizationList.length === 0) {
        return;
      }
      
      // PRIORITÉ 1 : Si un code organisation est dans l'URL (params ou pathname), l'utiliser
      if (codeToUse) {
        // Vérifier que le code existe dans la liste des organisations
        const orgFromUrl = user.organizationList.find((org) => org.code === codeToUse);
        if (orgFromUrl) {
          // Définir l'organisation depuis l'URL
          setOrganizationFromUrl(codeToUse);
          lastProcessedOrgCodeRef.current = codeToUse;
          return;
        } else {
          // Le code organisation dans l'URL n'existe pas dans la liste
          console.warn(`Organization code "${codeToUse}" not found in user's organization list`);
          // Utiliser la première organisation et rediriger vers elle
          const firstOrg = user.organizationList[0];
          if (firstOrg) {
            changeOrganizationByCode(firstOrg.code);
            lastProcessedOrgCodeRef.current = firstOrg.code;
            
            // Mettre à jour l'URL avec la première organisation valide
            const locale = getLocale();
            
            // Extraire le chemin sans le locale et l'organisation
            let pathWithoutLocaleAndOrg = currentPath;
            
            // Enlever le locale s'il est présent
            const pathParts = pathWithoutLocaleAndOrg.split('/').filter(p => p);
            if (pathParts.length > 0 && VALID_LOCALES.includes(pathParts[0])) {
              pathParts.shift();
            }
            // Enlever aussi l'ancien format avec juste le code organisation (valide ou invalide)
            if (pathParts.length > 0 && (pathParts[0] === codeToUse || pathParts[0] === firstOrg.code)) {
              pathParts.shift();
            }
            
            pathWithoutLocaleAndOrg = pathParts.length > 0 ? "/" + pathParts.join("/") : "/";
            
            const newPath = `/${locale}/organization/${firstOrg.code}${pathWithoutLocaleAndOrg}`;
            
            if (currentPath !== newPath) {
              history.replace(newPath);
            }
          }
          return;
        }
      }
      
      // PRIORITÉ 2 : Si aucun code organisation dans l'URL, utiliser la première organisation de la liste
      // Mais seulement si aucune organisation n'est déjà définie ou si elle ne correspond pas à l'URL
      const firstOrg = user.organizationList[0];
      if (firstOrg && (!currentOrganization || currentOrganization.code !== firstOrg.code)) {
        changeOrganizationByCode(firstOrg.code);
        lastProcessedOrgCodeRef.current = firstOrg.code;
        
        // Mettre à jour l'URL avec la première organisation
        const locale = getLocale();
        
        // Extraire le chemin sans le locale et l'organisation
        let pathWithoutLocaleAndOrg = currentPath;
        
        // Enlever le locale s'il est présent
        const pathParts = pathWithoutLocaleAndOrg.split('/').filter(p => p);
        if (pathParts.length > 0 && VALID_LOCALES.includes(pathParts[0])) {
          pathParts.shift();
        }
        // Enlever aussi l'ancien format avec juste le code organisation
        if (pathParts.length > 0 && pathParts[0] === firstOrg.code) {
          pathParts.shift();
        }
        
        pathWithoutLocaleAndOrg = pathParts.length > 0 ? "/" + pathParts.join("/") : "/";
        
        const newPath = `/${locale}/organization/${firstOrg.code}${pathWithoutLocaleAndOrg}`;
        
        if (currentPath !== newPath) {
          history.replace(newPath);
        }
      }
    } catch (error) {
      // Si l'utilisateur n'est pas encore chargé, ne pas faire d'erreur
      return;
    }
  }, [hasUser, organizationCodeFromUrl, location.pathname, getUser, setOrganizationFromUrl, changeOrganizationByCode, currentOrganization, history, getLocale]);

  // EFFET 2 : Mettre à jour l'URL quand l'organisation change (sélection manuelle)
  // Cet effet s'exécute quand currentOrganization change et que l'URL ne correspond pas
  useEffect(() => {
    if (!hasUser || !currentOrganization) {
      return;
    }
    
    const currentPath = location.pathname;
    if (!currentPath) {
      return;
    }
    
    // Ne pas rediriger pour les routes de login et d'erreur
    if (currentPath === "/login" || currentPath === "/404" || currentPath === "/error" || currentPath.startsWith("/login")) {
      return;
    }

    // Vérifier si l'URL contient des paramètres non résolus (signe d'un problème)
    if (currentPath.includes(':locale') || currentPath.includes(':organizationCode')) {
      // L'URL contient des paramètres non résolus, ne pas traiter
      console.error('URL contains unresolved parameters in EFFET 2:', currentPath);
      return;
    }
    
    const orgMatch = currentPath.match(/\/organization\/([^/]+)/);
    const currentOrgCodeInUrl = orgMatch ? orgMatch[1] : null;
    
    // Si le code organisation dans l'URL correspond déjà à l'organisation actuelle, ne rien faire
    if (currentOrgCodeInUrl === currentOrganization.code) {
      // Mettre à jour la référence pour éviter de retraiter
      lastProcessedOrgCodeRef.current = currentOrganization.code;
      return;
    }
    
    // Si l'URL ne correspond pas à l'organisation actuelle, on doit mettre à jour l'URL
    // Cela se produit lors d'un changement manuel d'organisation
    // On ignore lastProcessedOrgCodeRef car on veut toujours mettre à jour l'URL lors d'un changement manuel

    // Ne pas rediriger pour les routes de login et d'erreur
    if (currentPath === "/login" || currentPath === "/404" || currentPath === "/error" || currentPath.startsWith("/login")) {
      return;
    }

    // Construire le nouveau chemin avec le locale et l'organisation
    const locale = getLocale();
    
    // Extraire le chemin sans le locale et l'organisation
    let pathWithoutLocaleAndOrg = currentPath;
    
    // Si le chemin contient déjà /organization/, extraire ce qui vient après
    const orgIndex = pathWithoutLocaleAndOrg.indexOf("/organization/");
    if (orgIndex !== -1) {
      const afterOrg = pathWithoutLocaleAndOrg.substring(orgIndex + "/organization/".length);
      const orgCodeEnd = afterOrg.indexOf("/");
      if (orgCodeEnd !== -1) {
        pathWithoutLocaleAndOrg = afterOrg.substring(orgCodeEnd);
      } else {
        pathWithoutLocaleAndOrg = "/";
      }
    } else {
      // Sinon, enlever le locale s'il est présent
      const pathParts = pathWithoutLocaleAndOrg.split('/').filter(p => p);
      if (pathParts.length > 0 && VALID_LOCALES.includes(pathParts[0])) {
        pathParts.shift();
        pathWithoutLocaleAndOrg = "/" + pathParts.join("/");
      }
      // Enlever aussi l'ancien format avec juste le code organisation
      if (pathParts.length > 0 && pathParts[0] === currentOrganization.code) {
        pathParts.shift();
        pathWithoutLocaleAndOrg = "/" + pathParts.join("/");
      }
    }
    
    if (!pathWithoutLocaleAndOrg || pathWithoutLocaleAndOrg === "") {
      pathWithoutLocaleAndOrg = "/";
    }
    
    const newPath = `/${locale}/organization/${currentOrganization.code}${pathWithoutLocaleAndOrg}`;
    
    // Ne mettre à jour l'URL que si elle est différente
    if (currentPath !== newPath) {
      history.replace(newPath);
      // Mettre à jour la référence après avoir mis à jour l'URL
      lastProcessedOrgCodeRef.current = currentOrganization.code;
    }
  }, [hasUser, currentOrganization, location.pathname, history, getLocale]);

  return <>{children}</>;
};

